//original code
import React, { useState, useRef, useEffect } from 'react';
import '../css/WebCamLive.css';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import axios from "axios";
import API_URL from "../Config";

const BASE_URL = API_URL;
const iceServers = [{ urls: 'stun:stun.l.google.com:19302' }];

const WebCamLive = () => {

    const { state } = useLocation();
    const mediaRef = useRef(null);
    const stompRef = useRef(null);
    const peersRef = useRef(new Map());
    const iceQueueRef = useRef(new Map());
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState('Ready');
    const [viewerCount, setViewerCount] = useState(0);
    const navigate = useNavigate();

    const videoRef = useRef(null);
    const cameraStreamRef = useRef(null);
    const micStreamRef = useRef(null);

    const [isLive, setIsLive] = useState(false);
    const [micEnabled, setMicEnabled] = useState(false);
    const [cameraEnabled, setCameraEnabled] = useState(false);
    const [error, setError] = useState('');
    const [stream, setStream] = useState(null);

    const token = sessionStorage.getItem("tokenn");

    const location = useLocation();
    const { roomId, adminId, streamId } = location.state || {};



     // STEP 1: Fetch stream details
  useEffect(() => {
    let active = true;

    const fetchStream = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/streams/get/${streamId}`,
        );

        if (active) {
          setStream(response.data);
          setError("");
        }
      } catch (err) {
        console.error("Stream fetch error:", err);

        if (active) {
          setError("Unable to load live stream.");
        }
      }
    };

    if (streamId) {
      fetchStream();
    }

    return () => {
      active = false;
    };
  }, [streamId]);

  console.log('Stream details:', stream);

console.log('Room ID:', roomId);
console.log('Admin ID:', adminId);
console.log('Stream ID:', streamId);

 const closeViewer = (viewerId) => {
    const peer = peersRef.current.get(viewerId);
    if (peer) peer.close();
    peersRef.current.delete(viewerId);
    iceQueueRef.current.delete(viewerId);
  };

  const send = (type, target, payload = {}) => {
    if (!stompRef.current?.connected) return;
    stompRef.current.publish({
      destination: `/app/webrtc/${streamId}/signal`,
      body: JSON.stringify({ type, from: 'admin', target, ...payload })
    });
  };

    const makeOffer = async (viewerId) => {
    if (!mediaRef.current) return;
    closeViewer(viewerId);
    const peer = new RTCPeerConnection({ iceServers });
    peersRef.current.set(viewerId, peer);
    mediaRef.current.getTracks().forEach(track => peer.addTrack(track, mediaRef.current));
    peer.onicecandidate = ({ candidate }) => {
      if (candidate) send('ice', viewerId, { candidate: candidate.toJSON() });
    };
    peer.onconnectionstatechange = () => {
      if (peer.connectionState === 'failed' || peer.connectionState === 'closed') closeViewer(viewerId);
    };
    const offer = await peer.createOffer();
    await peer.setLocalDescription(offer);
    send('offer', viewerId, { sdp: peer.localDescription });
  };

  const handleSignal = async (msg) => {
    const viewerId = msg.from;
    if (!viewerId || viewerId === 'admin') return;
    if (msg.type === 'join') {
      await makeOffer(viewerId);
    } else if (msg.type === 'answer') {
      const peer = peersRef.current.get(viewerId);
      if (!peer || peer.signalingState !== 'have-local-offer') return;
      await peer.setRemoteDescription(new RTCSessionDescription(msg.sdp));
      for (const candidate of iceQueueRef.current.get(viewerId) || []) {
        await peer.addIceCandidate(new RTCIceCandidate(candidate));
      }
      iceQueueRef.current.delete(viewerId);
    } else if (msg.type === 'ice' && msg.candidate) {
      const peer = peersRef.current.get(viewerId);
      if (!peer) return;
      if (peer.remoteDescription) await peer.addIceCandidate(new RTCIceCandidate(msg.candidate));
      else iceQueueRef.current.set(viewerId, [...(iceQueueRef.current.get(viewerId) || []), msg.candidate]);
    } else if (msg.type === 'leave') {
      closeViewer(viewerId);
    }
  };

  const stopMediaAndPeers = () => {
    peersRef.current.forEach(peer => peer.close());
    peersRef.current.clear();
    iceQueueRef.current.clear();
    mediaRef.current?.getTracks().forEach(track => track.stop());
    mediaRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const startLive = async () => {
      if (busy || isLive) return;
      setError('');
      setBusy(true);
      try {
        if (!streamId || !adminId || !roomId || !token) throw new Error('Missing stream details or login token');
        if (!cameraEnabled && !micEnabled) throw new Error('Enable camera or microphone before starting');
        const media = await navigator.mediaDevices.getUserMedia({ video: cameraEnabled, audio: micEnabled });
        mediaRef.current = media;
        if (videoRef.current) videoRef.current.srcObject = media;
        const client = new Client({
          webSocketFactory: () => new SockJS(`${BASE_URL}/ws-livestream`),
          reconnectDelay: 0,
          onConnect: () => {
            client.subscribe(`/topic/webrtc/${streamId}/admin`, frame => {
              handleSignal(JSON.parse(frame.body)).catch(err => setError(err.message));
            });
            client.subscribe(`/topic/stream/${streamId}/status`, frame => {
              const data = JSON.parse(frame.body);
              setStatus(data.status);
              setIsLive(data.status === 'LIVE');
            });
            client.subscribe(`/topic/stream/${streamId}/viewer-count`, frame => {
              setViewerCount(JSON.parse(frame.body).count);
            });
            client.publish({
              destination: `/app/stream/${streamId}/start`,
              headers: { Authorization: `Bearer ${token}` },
              body: JSON.stringify({ adminId: Number(adminId), roomId })
            });
            setStatus('Waiting for backend confirmation');
          },
          onStompError: frame => setError(frame.body || 'STOMP error'),
          onWebSocketError: () => setError('WebSocket connection failed')
        });
        stompRef.current = client;
        client.activate();
      } catch (err) {
        stopMediaAndPeers();
        setError(err.message);
      } finally { setBusy(false); }
    };

     const stopLive = async () => {
        setBusy(true);
        try {
          // IMPORTANT: Secure this REST endpoint with authenticated admin authorization before deployment.
          const response = await axios.post(`${BASE_URL}/api/streams/${streamId}/stop`, null, {
            params: { adminId }, headers: { Authorization: `Bearer ${token}` }
          });
          setStatus(response.data.status);
          setIsLive(false);
          stompRef.current?.deactivate();
          stompRef.current = null;
          stopMediaAndPeers();
        } catch (err) { setError(err.response?.data?.message || err.message); }
        finally { setBusy(false); }
      };
    
      useEffect(() => () => {
        stompRef.current?.deactivate();
        peersRef.current.forEach(peer => peer.close());
        peersRef.current.clear();
        mediaRef.current?.getTracks().forEach(track => track.stop());
      }, []);


  const handleClick = (link) => {
    localStorage.removeItem('items');
    navigate(link);
  };

  // Display the camera stream inside the preview
  useEffect(() => {
    if (videoRef.current && cameraStreamRef.current) {
      videoRef.current.srcObject = cameraStreamRef.current;
    }
  }, [cameraEnabled]);

  // // Turn camera ON/OFF
  // const toggleCamera = async () => {
  //   setError('');

  //   if (cameraEnabled) {
  //     if (cameraStreamRef.current) {
  //       cameraStreamRef.current.getTracks().forEach((track) => {
  //         track.stop();
  //       });

  //       cameraStreamRef.current = null;
  //     }

  //     if (videoRef.current) {
  //       videoRef.current.srcObject = null;
  //     }

  //     setCameraEnabled(false);
  //     return;
  //   }

  //   try {
  //     const stream = await navigator.mediaDevices.getUserMedia({
  //       video: true,
  //       audio: false,
  //     });

  //     cameraStreamRef.current = stream;

  //     if (videoRef.current) {
  //       videoRef.current.srcObject = stream;
  //       await videoRef.current.play();
  //     }

  //     setCameraEnabled(true);
  //   } catch (err) {
  //     console.error('Camera error:', err);

  //     setError(
  //       err.name === 'NotAllowedError'
  //         ? 'Camera permission denied. Please allow camera access.'
  //         : 'Unable to access the camera. Check whether it is connected or being used by another application.'
  //     );
  //   }
  // };


  
const toggleCamera = async () => {
  setError("");

  // Turn camera OFF
  if (cameraEnabled) {
    // Stop the local preview camera
    cameraStreamRef.current?.getVideoTracks().forEach((track) => {
      track.stop();
    });

    cameraStreamRef.current = null;

    // Stop the camera track being broadcast to viewers
    const broadcastVideoTracks = mediaRef.current?.getVideoTracks() || [];

    broadcastVideoTracks.forEach((track) => {
      track.stop();
      mediaRef.current.removeTrack(track);
    });

    // Notify all WebRTC connections that video is disabled
    peersRef.current.forEach((peer) => {
      peer.getSenders().forEach((sender) => {
        if (sender.track?.kind === "video") {
          sender.replaceTrack(null).catch(console.error);
        }
      });
    });

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setCameraEnabled(false);
    return;
  }

  // Turn camera ON
  try {
    const cameraStream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: false,
    });

    const newVideoTrack = cameraStream.getVideoTracks()[0];

    cameraStreamRef.current = cameraStream;

    // Add the video track to the broadcast stream
    if (!mediaRef.current) {
      mediaRef.current = new MediaStream();
    }

    mediaRef.current.addTrack(newVideoTrack);

    // Replace or add the video sender for every viewer
    for (const peer of peersRef.current.values()) {
      const videoSender = peer
        .getSenders()
        .find((sender) => sender.track?.kind === "video");

      if (videoSender) {
        await videoSender.replaceTrack(newVideoTrack);
      } else {
        peer.addTrack(newVideoTrack, mediaRef.current);
      }
    }

    if (videoRef.current) {
      videoRef.current.srcObject = cameraStream;
      await videoRef.current.play();
    }

    setCameraEnabled(true);
  } catch (err) {
    console.error("Camera error:", err);

    setError(
      err.name === "NotAllowedError"
        ? "Camera permission denied. Please allow camera access."
        : "Unable to access the camera."
    );
  }
};


  // Turn microphone ON/OFF
  const toggleMicrophone = async () => {
    setError('');

    if (micEnabled) {
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((track) => {
          track.stop();
        });

        micStreamRef.current = null;
      }

      setMicEnabled(false);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: false,
        audio: true,
      });

      micStreamRef.current = stream;
      setMicEnabled(true);
    } catch (err) {
      console.error('Microphone error:', err);

      setError(
        err.name === 'NotAllowedError'
          ? 'Microphone permission denied. Please allow microphone access.'
          : 'Unable to access the microphone. Check your microphone connection.'
      );
    }
  };

  // UI button only; actual broadcasting is not implemented yet
  const handleStartLive = () => {
    setIsLive((previous) => !previous);
  };

  // Release camera and microphone when leaving the page
  useEffect(() => {
    return () => {
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => {
          track.stop();
        });
      }

      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((track) => {
          track.stop();
        });
      }
    };
  }, []);

  
// const handleStart = () => {
//     console.log("Start button clicked");

//     console.log("roomId:", roomId);
//     console.log("adminId:", adminId);

//     if (!roomId || !adminId) {
//         console.error("Missing stream details:", { roomId, adminId });
//         setError("Missing stream details.");
//         return;
//     }

//     console.log("Connecting to WebSocket...");

//     const client = new Client({
//         webSocketFactory: () =>
//             new SockJS("http://localhost:8080/ws-livestream"),

//         debug: (message) => {
//             console.log("[STOMP DEBUG]", message);
//         },

//         onConnect: () => {
//             console.log("WebSocket connected successfully!");

//             const request = {
//                 adminId: Number(adminId),
//                 roomId: roomId
//             };

//             console.log("Sending start request:", request);

//             client.publish({
//                 destination: `/app/stream/1/start`,
//                 body: JSON.stringify(request)
//             });

//             console.log("Start request sent successfully.");
//             setIsLive(true);
//         },

//         onStompError: (frame) => {
//             console.error("STOMP error:", frame.headers.message);
//             console.error("Error details:", frame.body);
//             setError("Unable to start the live stream.");
//         },

//         onWebSocketError: (error) => {
//             console.error("WebSocket connection error:", error);
//         },

//         onWebSocketClose: (event) => {
//             console.log("WebSocket closed:", event);
//         }
//     });

//     client.activate();
// };


const handleStart = () => {
    console.log("Start button clicked");

    console.log("roomId:", roomId);
    console.log("adminId:", adminId);

    if (!roomId || !adminId) {
        console.error("Missing stream details:", { roomId, adminId });
        setError("Missing stream details.");
        return;
    }

    if (!token) {
        console.error("JWT token missing");
        setError("Please login again.");
        return;
    }

    console.log("Connecting to WebSocket...");

    const client = new Client({
        webSocketFactory: () =>
            new SockJS("http://localhost:8080/ws-livestream"),

        reconnectDelay: 5000,

        debug: (message) => {
            console.log("[STOMP DEBUG]", message);
        },

        onConnect: () => {
            console.log("WebSocket connected successfully!");

            const request = {
                adminId: Number(adminId),
                roomId: roomId
            };

            console.log("Sending start request:", request);

            client.publish({
                destination: `/app/stream/${streamId}/start`,

                headers: {
                    Authorization: `Bearer ${token}`
                },

                body: JSON.stringify(request)
            });

            console.log("Start request sent successfully.");
            // Do not setIsLive(true) here.
            // Wait for the backend to confirm that the stream started.
        },

        onStompError: (frame) => {
            console.error("STOMP error:", frame.headers.message);
            console.error("Error details:", frame.body);
            setError("Unable to start the live stream.");
        },

        onWebSocketError: (error) => {
            console.error("WebSocket connection error:", error);
        },

        onWebSocketClose: (event) => {
            console.log("WebSocket closed:", event);
        }
    });

    client.activate();
};


  return (
    <div className="marquee-container">

      <div className="AddArea">
        <button
          className="btn btn-custom"
          onClick={() => handleClick('/admin/upload-content')}
        >
          Upload Content
        </button>
      </div>

      <br />

      <div className="container3">
        <ol className="breadcrumb mb-4 d-flex my-0">
          <li className="breadcrumb-item text-white">
            WebCamLive Streaming
          </li>
        </ol>

        {/* Main webcam content */}
        <div className="webcam-main">

          {/* Webcam preview and controls */}
          <div className="webcam-left">

            <div className="webcam-preview">

              {/* Actual camera video */}
              <video
                ref={videoRef}
                className={`webcam-video ${
                  cameraEnabled ? 'video-visible' : ''
                }`}
                autoPlay
                playsInline
                muted
              />

              {/* Camera and microphone controls */}
              <div className="webcam-controls" >

                {/* Microphone button */}
                <button
                  type="button"
                  className={`webcam-icon ${
                    micEnabled ? 'enabled' : ''
                  }`}
                  onClick={toggleMicrophone}
                  title={
                    micEnabled
                      ? 'Turn microphone off'
                      : 'Turn microphone on'
                  }
                  aria-label="Toggle microphone"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect
                      x="9"
                      y="2"
                      width="6"
                      height="12"
                      rx="3"
                    />
                    <path d="M5 10v2a7 7 0 0 0 14 0v-2" />
                    <path d="M12 19v3M8 22h8" />
                  </svg>

                  {!micEnabled && <span className="slash" />}
                </button>

                {/* Camera button */}
                <button
                  type="button"
                  className={`webcam-icon ${
                    cameraEnabled ? 'enabled' : ''
                  }`}
                  onClick={toggleCamera}
                  title={
                    cameraEnabled
                      ? 'Turn camera off'
                      : 'Turn camera on'
                  }
                  aria-label="Toggle camera"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect
                      x="3"
                      y="6"
                      width="12"
                      height="12"
                      rx="1"
                    />
                    <path d="m15 10 6-4v12l-6-4" />
                  </svg>

                  {!cameraEnabled && <span className="slash" />}
                </button>

              </div>
            </div>

            {/* Error message */}
            {error && (
              <p className="webcam-error" role="alert">
                {error}
              </p>
            )}

            {/* Start Live button */}
            <button
              type="button"
              className={`start-live-btn ${
                isLive ? 'stop-live-btn' : ''
              }`}
              onClick={isLive ? stopLive : startLive}
            >
              <span>◉</span>
              {isLive ? 'Stop Live' : 'Start Live'}
            </button>

          </div>

          {/* Stream details */}
          <div className="webcam-details">

            <div className="detail-row">
              <span className="detail-label">Video Title</span>
              <span>:</span>
              <span className="detail-value">{stream?.title || "Stream Title"}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Category</span>
              <span>:</span>
              <span className="detail-value">{stream?.category || "Live Music"}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Tags</span>
              <span>:</span>
              <span className="detail-value">
                {stream?.tags || "No tags available."}
              </span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Schedule</span>
              <span>:</span>
              <span className="detail-value">
                {stream?.scheduledStart}
              </span>
            </div>

            <div className="detail-row">
              <span className="detail-label">EndTime</span>
              <span>:</span>
              <span className="detail-value">
                {stream?.scheduledEnd}
              </span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Webcam Source</span>
              <span>:</span>
              <span className="detail-value">{stream?.webcamSource || "Default Camera"}</span>
            </div>


            <div className="detail-row description-row">
              <span className="detail-label">Description</span>
              <span>:</span>
              <span className="detail-value">
                {stream?.description || "No description available."}
              </span>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default WebCamLive;




// import React, { useEffect, useRef, useState } from "react";
// import { Client } from "@stomp/stompjs";
// import SockJS from "sockjs-client";
// import axios from "axios";

// const WebCamLive = () => {
//     const [streamId, setStreamId] = useState(null);
//     const [roomId, setRoomId] = useState("");
//     const [adminId, setAdminId] = useState(null);

//     const [isConnected, setIsConnected] = useState(false);
//     const [isLive, setIsLive] = useState(false);
//     const [error, setError] = useState("");
//     const [statusMessage, setStatusMessage] = useState(
//         "Create a stream before starting live."
//     );

//     const clientRef = useRef(null);
//     const pendingStartRef = useRef(false);

//     // Replace this with the admin ID from your authenticated user/session.
//     // Do not hardcode an admin ID in production.
//     const currentAdminId = null;

//     // Connect to the Spring Boot STOMP endpoint.
//     useEffect(() => {
//         const client = new Client({
//             webSocketFactory: () =>
//                 new SockJS("http://localhost:8080/ws-livestream"),

//             reconnectDelay: 5000,

//             debug: (message) => {
//                 console.log("[STOMP]", message);
//             },

//             onConnect: () => {
//                 console.log("WebSocket connected successfully.");
//                 setIsConnected(true);
//                 setError("");
//                 setStatusMessage("Connected to the live server.");

//                 // Subscribe to the stream status updates.
//                 client.subscribe(
//                     "/topic/live-notifications",
//                     (message) => {
//                         try {
//                             const stream = JSON.parse(message.body);

//                             console.log(
//                                 "Live notification received:",
//                                 stream
//                             );

//                             if (
//                                 stream.id === streamId ||
//                                 stream.roomId === roomId
//                             ) {
//                                 if (stream.status === "LIVE") {
//                                     setIsLive(true);
//                                     setStatusMessage("Your stream is LIVE.");
//                                 }
//                             }
//                         } catch (err) {
//                             console.error(
//                                 "Unable to parse notification:",
//                                 err
//                             );
//                         }
//                     }
//                 );

//                 // Send a start request if the button was clicked
//                 // while the connection was still being established.
//                 if (pendingStartRef.current) {
//                     pendingStartRef.current = false;
//                     publishStart(client);
//                 }
//             },

//             onStompError: (frame) => {
//                 console.error(
//                     "STOMP error:",
//                     frame.headers.message,
//                     frame.body
//                 );
//                 setError("The server rejected the WebSocket message.");
//                 setStatusMessage("Unable to start the stream.");
//             },

//             onWebSocketError: (event) => {
//                 console.error("WebSocket error:", event);
//                 setError("Unable to connect to the live server.");
//             },

//             onWebSocketClose: () => {
//                 console.log("WebSocket connection closed.");
//                 setIsConnected(false);
//             }
//         });

//         clientRef.current = client;
//         client.activate();

//         return () => {
//             clientRef.current = null;
//             client.deactivate();
//         };
//     }, [streamId, roomId]);

//     // Example: create and save a stream before starting it.
//     // Call this function from your existing form submit handler.
//     const createStream = async (formData) => {
//         try {
//             setError("");
//             setStatusMessage("Creating stream...");

//             const response = await axios.post(
//                 "http://localhost:8080/api/streams/webcamlive",
//                 formData
//             );

//             const savedStream = response.data;

//             console.log("Stream saved successfully:", savedStream);
//             console.log("Database stream ID:", savedStream.id);
//             console.log("Room ID:", savedStream.roomId);

//             setStreamId(savedStream.id);
//             setRoomId(savedStream.roomId);
//             setAdminId(savedStream.adminId);

//             setStatusMessage(
//                 "Stream created. You can now start live."
//             );
//         } catch (err) {
//             console.error(
//                 "Failed to create stream:",
//                 err.response?.data || err.message
//             );
//             setError("Failed to create the stream.");
//             setStatusMessage("Stream creation failed.");
//         }
//     };

//     // Publish the STOMP message.
//     const publishStart = (client) => {
//         if (!client || !client.connected) {
//             console.error("STOMP client is not connected.");
//             setError("WebSocket is not connected.");
//             return;
//         }

//         const request = {
//             adminId: Number(adminId),
//             roomId: roomId
//         };

//         console.log("Sending start request:", {
//             streamId,
//             ...request
//         });

//         client.publish({
//             destination: `/app/stream/${streamId}/start`,
//             body: JSON.stringify(request)
//         });

//         console.log("Start request published.");
//         setStatusMessage("Start request sent. Waiting for server update...");
//     };

//     const handleStart = () => {
//         setError("");

//         if (!streamId || !roomId || !adminId) {
//             console.error("Missing stream details:", {
//                 streamId,
//                 roomId,
//                 adminId
//             });

//             setError("Please create the stream first.");
//             return;
//         }

//         if (Number(adminId) <= 0 || !Number.isFinite(Number(adminId))) {
//             setError("Invalid admin ID.");
//             return;
//         }

//         console.log("Starting stream:", {
//             streamId,
//             roomId,
//             adminId
//         });

//         const client = clientRef.current;

//         if (!client || !client.connected) {
//             if (client && client.active) {
//                 pendingStartRef.current = true;
//                 setStatusMessage("Connecting to server...");
//             } else {
//                 setError("WebSocket is not active. Please try again.");
//             }
//             return;
//         }

//         publishStart(client);
//     };

//     return (
//         <div style={{ padding: "24px" }}>
//             <h2>Webcam Live Streaming</h2>

//             <p>
//                 WebSocket status:{" "}
//                 <strong>
//                     {isConnected ? "Connected" : "Disconnected"}
//                 </strong>
//             </p>

//             <p>Stream ID: {streamId ?? "Not created"}</p>
//             <p>Room ID: {roomId || "Not created"}</p>
//             <p>Admin ID: {adminId ?? "Not available"}</p>

//             <p>
//                 Stream status:{" "}
//                 <strong>{isLive ? "LIVE" : "Not live"}</strong>
//             </p>

//             <p>{statusMessage}</p>

//             {error && (
//                 <p style={{ color: "red" }}>
//                     {error}
//                 </p>
//             )}

//             <button
//                 type="button"
//                 onClick={handleStart}
//                 disabled={!isConnected || isLive}
//             >
//                 {isLive ? "Live Started" : "Start Live"}
//             </button>
//         </div>
//     );
// };

// export default WebCamLive;
